import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ShieldCheck, KeyRound, RefreshCw, AlertTriangle, Copy, CheckCircle2, Loader2, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR"); }
  catch { return iso; }
}
function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso).getTime();
    const diff = d - Date.now();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days <= 0) return "expirée";
    if (days === 1) return "demain";
    return `dans ${days} jours`;
  } catch { return iso; }
}

export default function SecuritySettings() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null); // { secret, provisioning_uri }
  const [setupCode, setSetupCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/auth/2fa/status");
      setStatus(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup/start");
      setSetup(r.data);
      setSetupCode("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const verifyEnroll = async () => {
    if (setupCode.trim().length < 6) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup/verify",
        { code: setupCode.trim() });
      toast.success("2FA activée avec succès.");
      setShowBackupCodes(r.data.backup_codes);
      setSetup(null);
      setSetupCode("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const copyCodes = () => {
    if (!showBackupCodes) return;
    navigator.clipboard.writeText(showBackupCodes.join("\n"));
    toast.success("Codes copiés dans le presse-papier.");
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
    </div>
  );

  const enabled = status?.enabled;
  const required = status?.required;
  const graceDeadline = status?.grace_deadline;
  const graceExpired = status?.grace_expired;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="security-settings-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
          Sécurité du compte
        </div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          Authentification à deux facteurs
        </h1>
        <p className="mt-1 text-sm text-slate-500 max-w-2xl">
          Ajoutez une deuxième couche de protection à votre compte avec une application d&apos;authentification standard (Google Authenticator, 1Password, Authy…). Aucune donnée de type SMS ou email ne fait office de secours — uniquement TOTP + codes de récupération à usage unique.
        </p>
      </div>

      {/* Enrollment banner */}
      {required && !enabled && (
        <Alert variant={graceExpired ? "destructive" : "default"}
               className={cn("border-l-4",
                             graceExpired ? "border-red-500" : "border-amber-500")}
               data-testid="2fa-required-banner">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {graceExpired ? (
              <><strong>Enrôlement 2FA obligatoire</strong> — Votre session est limitée jusqu&apos;à l&apos;activation. Suivez la procédure ci-dessous pour retrouver un accès complet.</>
            ) : (
              <><strong>Enrôlement obligatoire</strong> pour votre rôle. Grâce accordée : <strong>{fmtRelative(graceDeadline)}</strong> ({fmtDateTime(graceDeadline)}). Passée cette date, seule cette page restera accessible.</>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* State card */}
      <Card className="p-6" data-testid="2fa-state-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={cn(
              "h-10 w-10 rounded-full flex items-center justify-center shrink-0",
              enabled ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500"
            )}>
              <ShieldCheck className="h-5 w-5" strokeWidth={2} />
            </div>
            <div>
              <div className="font-medium text-slate-900">
                {enabled ? "2FA activée" : "2FA désactivée"}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {enabled
                  ? `Activée le ${fmtDateTime(status?.enrolled_at)}. ${status?.backup_codes_remaining ?? 0} codes de récupération restants.`
                  : (required ? "Requise pour votre rôle." : "Optionnelle pour votre rôle.")}
              </div>
            </div>
          </div>
          <Badge variant="outline" className={cn(
            "text-[10px]",
            enabled ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : required ? "border-amber-200 bg-amber-50 text-amber-800"
                               : "border-slate-200 bg-slate-50 text-slate-600"
          )}>
            {enabled ? "Active" : required ? "Requise" : "Optionnelle"}
          </Badge>
        </div>

        {/* Action buttons */}
        {!enabled && !setup && (
          <Button onClick={startEnroll} disabled={busy}
                  className="mt-5" data-testid="2fa-start-enroll-btn">
            <KeyRound className="h-4 w-4 mr-1.5" strokeWidth={2} />
            Activer la 2FA
          </Button>
        )}
        {enabled && (
          <div className="mt-5 flex flex-wrap gap-2">
            <Button variant="outline"
                    onClick={async () => {
                      const code = window.prompt("Saisissez un code TOTP pour régénérer les codes de récupération :");
                      if (!code) return;
                      setBusy(true);
                      try {
                        const r = await api.post("/auth/2fa/backup-codes/regenerate",
                          { code: code.trim() });
                        setShowBackupCodes(r.data.backup_codes);
                        toast.success("Nouveaux codes de récupération générés.");
                        await load();
                      } catch (e) { toast.error(apiError(e)); }
                      finally { setBusy(false); }
                    }}
                    disabled={busy}
                    data-testid="2fa-regenerate-backup-btn">
              <RefreshCw className="h-4 w-4 mr-1.5" strokeWidth={2} />
              Régénérer les codes de récupération
            </Button>
            {!required && (
              <Button variant="outline"
                      onClick={async () => {
                        const pwd = window.prompt("Mot de passe :");
                        if (!pwd) return;
                        const code = window.prompt("Code TOTP :");
                        if (!code) return;
                        setBusy(true);
                        try {
                          await api.post("/auth/2fa/disable",
                            { password: pwd, code: code.trim() });
                          toast.success("2FA désactivée.");
                          await load();
                        } catch (e) { toast.error(apiError(e)); }
                        finally { setBusy(false); }
                      }}
                      disabled={busy}
                      className="text-red-600 hover:text-red-700"
                      data-testid="2fa-disable-btn">
                <Trash2 className="h-4 w-4 mr-1.5" strokeWidth={2} />
                Désactiver
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Enrollment card — QR + verify */}
      {setup && (
        <Card className="p-6 space-y-5" data-testid="2fa-enroll-card">
          <div>
            <div className="font-medium text-slate-900 mb-1">Étape 1 — Scannez le QR code</div>
            <div className="text-xs text-slate-500">
              Utilisez votre application d&apos;authentification (Google Authenticator, 1Password, Authy…).
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="p-4 bg-white border border-slate-200 rounded-lg">
              <QRCodeSVG value={setup.provisioning_uri} size={192} level="M" />
            </div>
            <div className="flex-1 space-y-2">
              <Label className="text-xs text-slate-500">
                Ou saisissez la clé manuellement :
              </Label>
              <div className="font-mono text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2 break-all">
                {setup.secret}
              </div>
              <div className="text-xs text-slate-500">
                Nom du compte : <span className="font-mono">Averdy Agency</span>
              </div>
            </div>
          </div>

          <div>
            <div className="font-medium text-slate-900 mb-1">Étape 2 — Saisissez le code affiché</div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto] gap-2 mt-2">
              <Input
                value={setupCode}
                onChange={(e) => setSetupCode(e.target.value)}
                placeholder="6 chiffres"
                data-testid="2fa-enroll-code-input"
                className="h-11 tracking-widest font-mono"
                maxLength={6}
              />
              <Button onClick={verifyEnroll}
                      disabled={busy || setupCode.trim().length < 6}
                      data-testid="2fa-enroll-verify-btn"
                      className="h-11">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                Confirmer
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Backup codes reveal — one-time-only */}
      {showBackupCodes && (
        <Card className="p-6 border-amber-300 bg-amber-50" data-testid="backup-codes-card">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" strokeWidth={2} />
            <div className="flex-1">
              <div className="font-semibold text-amber-900">
                Copiez ces codes de récupération MAINTENANT
              </div>
              <div className="text-xs text-amber-800 mt-1">
                Ils ne seront plus jamais affichés. Chaque code n&apos;est utilisable qu&apos;une seule fois. Conservez-les dans votre gestionnaire de mots de passe ou imprimez-les.
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 font-mono text-sm">
                {showBackupCodes.map((c, i) => (
                  <div key={i} className="bg-white border border-amber-200 rounded px-3 py-2 text-center"
                       data-testid={`backup-code-${i}`}>
                    {c}
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" onClick={copyCodes}
                        data-testid="backup-codes-copy-btn">
                  <Copy className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
                  Copier tous les codes
                </Button>
                <Button variant="ghost" size="sm"
                        onClick={() => setShowBackupCodes(null)}
                        data-testid="backup-codes-dismiss-btn">
                  J&apos;ai bien sauvegardé les codes
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
